import React, { useEffect, Suspense } from 'react';
import PartialCTA from '../partials/cta';
import featuredArticles from '~/data/sidebar/featured_articles.json';
import { useImageLoaded } from '~/hooks/useImage';
import HeroSubmenu from '../../fragments/hero-submenu';
import FormBuilder from '../form_builder';
import Stars from '../stars';
import { graphql } from 'gatsby';
import { GRID_BREAKPOINTS } from '~/utils/constants';
import { useWidth } from '~/hooks/use-width';
import { RatingLabeled } from '../rating/rating';
import { cn, getSettingsClass } from '~/utils/helpers';
import { useImageLoad } from '~/utils/image-load-provider';
import Numbers from '../numbers';
import { useViewport } from '~/utils/viewport-provider';
const RiveImage = React.lazy(() => import('../../fragments/riveImage'));

interface HeroProps {
  block: {
    _template: string;
    settings: any;
    title: string;
    subtitle: string;
    subtitle_mobile: string;
    intro_text: string;
    rive_image: string;
    gap: number;
    rive_state_machine_name: string;
    rive_autoplay: boolean;
    image: string;
    image_alt_text: string;
    image_max_width?: number;
    image_position_class: string;
    text_position_class: string;
    text_width: string;
    center_text_on_mobile: boolean;
    show_additional_items_before_cta: boolean;
    hide_image_on_mobile: boolean;
    cta_field: any;
    additional_items: AdditionalItem[];
    submenu_field: SubmenuField;
    css_class_name: string;
  };
  isFront: boolean;
  parentPage?: {
    frontmatter: {
      url_alias: string;
      sections: {
        blocks: {
          submenu_field?: SubmenuField;
        }[];
      }[];
    };
  };
  article?: any;
  vars: any;
}

type CommonAdditionalItemProps = {
  _template: string;
};

type LogosAdditionalItem = {
  logos: LogosItem;
};

type StarsAdditionalItem = {
  theme: string;
  stars: number;
};

type RatingAdditionalItem = {
  theme: string;
  platforms: any[];
  justify_content: string;
};

type LabeledRatingAdditionalItem = {
  theme: string;
  platforms: any[];
  justify_content: string;
};

type AdditionalItem = CommonAdditionalItemProps &
  (
    | LogosAdditionalItem
    | StarsAdditionalItem
    | RatingAdditionalItem
    | LabeledRatingAdditionalItem
  );

interface AdditionalItemsProps {
  items: any[];
  vars: any;
}

type LogosItem = {
  title: string;
  icon: string;
};

const Hero = ({ block, isFront, parentPage, article, vars }: HeroProps) => {
  const settingsClass = getSettingsClass(block.settings);
  const { isDesktop } = useViewport();

  let logoBlock: LogosItem | undefined;
  if (block.additional_items && block.additional_items.length > 0) {
    logoBlock = block.additional_items.find(
      item => item._template === 'logo'
    ) as LogosItem | undefined;
  }

  let parentTitle;
  let parentUrl;
  let submenuField = block.submenu_field;

  if (parentPage) {
    const firstBlock = parentPage.frontmatter.sections[0]?.blocks[0];
    if (firstBlock?.submenu_field) {
      parentTitle = firstBlock.submenu_field.menu_group_title;
      parentUrl = parentPage.frontmatter.url_alias;
      submenuField = firstBlock.submenu_field;
    }
  }

  let heroTitle = block.title;
  let heroSubtitle = block.subtitle;
  let heroIntroText = block.intro_text;
  let heroImageData = {
    block: {
      rive_image: block.rive_image,
      rive_state_machine_name: block.rive_state_machine_name,
      rive_autoplay: block.rive_autoplay,
      image: block.image,
      image_alt_text: block.image_alt_text,
    } as {
      rive_image: string;
      rive_state_machine_name: string;
      rive_autoplay: boolean;
      image: string;
      image_alt_text: string;
      image_max_width?: number;
    },
  };

  if (block.image_max_width) {
    heroImageData.block.image_max_width = block.image_max_width;
  }

  if (article) {
    heroSubtitle = article.Title;
    heroIntroText = article.Description;
    const featured = featuredArticles.featured_categories.find(
      data => data.category === article.Category
    );
    if (featured) {
      heroImageData.block.image = featured.article.image;
      heroImageData.block.image_alt_text = featured.article.image_alt_text;
    }
  }

  const isFullWidthImage = block.image_position_class === 'bg-fullwidth';
  const imageWidth = block.text_width === 'Wide' ? 'col-lg-4' : 'col-lg-6';
  const contentWidth = block.text_width === 'Wide' ? 'col-lg-8' : 'col-lg-6';
  const showImageOnMobile =
    isFullWidthImage === false && block.hide_image_on_mobile === false;
  const classString = cn(
    `block ${block._template} ${block.css_class_name} ${
      block.image_position_class ? block.image_position_class : 'bg-right'
    } ${block.text_position_class} ${block.image ? 'has-image' : ''} ${
      block.center_text_on_mobile ? ' mobile-text-center' : ''
    } ${submenuField?.menu_items?.length > 0 ? ' has-submenu' : ''} ${settingsClass}`
  );

  return (
    <section className={classString}>
      <div className='container'>
        <HeroSubmenu
          block={submenuField}
          parentTitle={parentTitle}
          parentUrl={parentUrl}
        />
        <div className='row'>
          <div
            className={`hero-col ${contentWidth}`}
            style={{
              paddingRight: isDesktop && block.gap ? `${block.gap}rem` : '',
            }}
          >
            {logoBlock && (
              <Logo
                item={logoBlock}
                type='hero'
              />
            )}
            {!logoBlock && heroTitle && (
              <h1 dangerouslySetInnerHTML={{ __html: heroTitle }}></h1>
            )}
            {block.subtitle_mobile ? (
              isFront ? (
                <>
                  <h1
                    className={`is-desktop`}
                    dangerouslySetInnerHTML={{ __html: block.subtitle }}
                  ></h1>
                  <h1
                    className={`is-mobile`}
                    dangerouslySetInnerHTML={{ __html: block.subtitle_mobile }}
                  ></h1>
                </>
              ) : (
                <>
                  <h2
                    className={`is-desktop`}
                    dangerouslySetInnerHTML={{ __html: block.subtitle }}
                  ></h2>
                  <h2
                    className={`is-mobile`}
                    dangerouslySetInnerHTML={{ __html: block.subtitle_mobile }}
                  ></h2>
                </>
              )
            ) : isFront ? (
              heroSubtitle && (
                <h1
                  dangerouslySetInnerHTML={{ __html: heroSubtitle }}
                  className={`${!!article ? 'h1--article' : ''}`}
                ></h1>
              )
            ) : (
              heroSubtitle && (
                <h2 dangerouslySetInnerHTML={{ __html: heroSubtitle }}></h2>
              )
            )}
            {showImageOnMobile ? (
              <HeroImage
                className='is-mobile'
                block={heroImageData.block}
              />
            ) : null}
            {heroIntroText && (
              <p
                className='intro'
                dangerouslySetInnerHTML={{ __html: heroIntroText }}
              ></p>
            )}
            {block.show_additional_items_before_cta && (
              <AdditionalItems
                items={block.additional_items}
                vars={vars}
              />
            )}
            {block.cta_field && block.cta_field.cta_url && (
              <div
                className={`cta-wr ${
                  block.cta_field?.subtext_right === true ? 'subtext-right' : ''
                }`}
              >
                <PartialCTA data={block.cta_field} />
              </div>
            )}
            {!block.show_additional_items_before_cta && (
              <AdditionalItems
                items={block.additional_items}
                vars={vars}
              />
            )}
          </div>
          {!isFullWidthImage ? (
            <div className={`hero-image-col ${imageWidth}`}>
              <HeroImage
                className={`is-desktop`}
                block={heroImageData.block}
              />
            </div>
          ) : null}
        </div>
        {isFullWidthImage ? (
          <HeroImage
            className={`is-desktop`}
            block={heroImageData.block}
          />
        ) : null}
      </div>
    </section>
  );
};

const AdditionalItems = ({ items, vars }: AdditionalItemsProps) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      {items.map((block, index) => {
        switch (block._template) {
          case 'block__numbers':
            return (
              <Numbers
                key={index}
                block={block}
                siteVars={vars}
              />
            );
          case 'subscription_field':
            return (
              <FormBuilder
                key={index}
                form={{
                  type: 'subscription',
                  action: block.cta_url,
                }}
                fields={[
                  {
                    type: 'email',
                    placeholder: block.input_placeholder,
                  },
                  {
                    type: 'submit',
                    text: block.cta_text,
                  },
                  {
                    type: 'terms',
                    text: block.terms_text,
                  },
                ]}
              />
            );
          case 'logos':
            return (
              <Logos
                key={index}
                items={block.logos}
              />
            );
          case 'block__stars':
            return (
              <Stars
                key={index}
                block={block}
                vars={vars}
              />
            );
          case 'block__rating_labeled':
            return (
              <RatingLabeled
                key={index}
                theme={block.theme}
                platforms={block.platforms}
                justify_content={block.justify_content}
                vars={vars}
              />
            );
          default: // no-op
            return undefined;
        }
      })}
    </>
  );
};

const Logos = ({ items }: { items: LogosItem[] }) => {
  return (
    <div className='hero-companies'>
      {items.map(item => (
        <Logo
          key={item.icon}
          item={item}
          type='companies'
        />
      ))}
    </div>
  );
};

const Logo = ({ item, type }: { item: LogosItem; type: string }) => {
  return (
    <div className={`logo logo--${type}`}>
      <img
        src={item.icon}
        alt={item.title}
        loading='lazy'
      />
    </div>
  );
};

/**
 * Hero Images hide on mobile by default (uses class "is-desktop")
 */
const HeroImage = ({
  block,
  className = '',
}: {
  block: any;
  className?: string;
}) => {
  const { setImagesLoaded } = useImageLoad();

  const style = block.image_max_width
    ? { style: { maxWidth: `${block.image_max_width}%` } }
    : {};
  const type =
    block.rive_image && block.rive_image.length > 0
      ? 'animated'
      : block.image
        ? 'img'
        : null;
  const [ref, loaded, onLoad] = useImageLoaded();
  const { width } = useWidth();

  const handleImageLoad = () => {
    setImagesLoaded(true);
  };

  useEffect(() => {
    const shouldLoad = width > GRID_BREAKPOINTS.md;
    if (!loaded && shouldLoad && type === 'img') {
      // preload image
      const image = new Image();
      image.src = block.image;
      image.fetchPriority = 'high';
      image.onload = onLoad;
    }
  }, [width]);

  if (width <= GRID_BREAKPOINTS.md) {
    return null;
  }

  if (type === 'animated') {
    return (
      <>
        <Suspense fallback={<div className='hero-cover-image'></div>}>
          <RiveImage
            block={block}
            classNames={`hero-cover-image ${className}`}
          />
        </Suspense>
      </>
    );
  }

  if (type === 'img' && loaded) {
    return (
      <img
        ref={ref}
        src={block.image}
        alt={block.image_alt_text}
        title={block.image_alt_text}
        className={`hero-cover-image ${className}`}
        onLoad={handleImageLoad}
        {...style}
      />
    );
  }

  return null;
};

export const subscriptionFragment = graphql`
  fragment SubscriptionFieldFragment on SubscriptionField {
    cta_text
    cta_url
    input_placeholder
    terms_text
    _template
  }
`;

export const logoFragment = graphql`
  fragment LogoFragment on Logo {
    title
    icon
    _template
  }
`;

export const logosFragment = graphql`
  fragment LogosFragment on Logos {
    logos {
      title
      icon
    }
    _template
  }
`;

export const blockNumbersFragment = graphql`
  fragment BlockNumbersFragment on BlockNumbers {
    _template
    settings {
      ...SettingsFragment
    }
    background_color
    value_font_size
    value_line_height
    text_font_size
    text_line_height
    text_alignment
    container_alignment
    column_gap
    columns {
      value
      text
      icon
      divider_position
    }
  }
`;

export const additionalItemsFragment = graphql`
  fragment AdditionalItemsFragment on AdditionalItem {
    ...SubscriptionFieldFragment
    ...LogoFragment
    ...LogosFragment
    ...StarsFragment
    ...LabeledRatingAdditionalFragment
    ...BlockNumbersFragment
  }
`;

export default Hero;
